import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { LocationQuery } from 'vue-router'

// 丸ゴ(LEC-GMaruGo)への切替。フォント定義は App.vue の :root.is-webview 側にある
const applyWebViewFontClass = (isWebView: boolean) => {
  document.documentElement.classList.toggle('is-webview', isWebView)
}

const WEBVIEW_MODE_STORAGE_KEY = 'KuraemonCloud2_stores.webViewMode'

// Pinia 初期化前（main.ts のプラグイン読み込み時など）でも WebView モードかどうかを判定する
// ハッシュルーターのため、初回オープン時のクエリは location.hash 内にある
export const detectWebViewModeEnv = (): boolean => {
  const hashQuery = window.location.hash.split('?')[1] ?? ''
  if (new URLSearchParams(hashQuery).get('webview') === '1') return true
  if (new URLSearchParams(window.location.search).get('webview') === '1') return true
  // リロード時（クエリなし）は persist 済みストアから復元
  try {
    const stored = sessionStorage.getItem(WEBVIEW_MODE_STORAGE_KEY)
    return !!stored && JSON.parse(stored).platform != null
  } catch {
    return false
  }
}

export const useWebViewModeStore = defineStore('webViewMode', () => {
  const platform = ref<'ios' | 'android' | null>(null)
  const orientation = ref<'portrait' | 'landscape'>('portrait')

  const isWebViewMode = computed(() => platform.value !== null)
  const isPortrait = computed(() => orientation.value === 'portrait')
  const isLandscape = computed(() => orientation.value === 'landscape')

  const initFromQuery = (query: LocationQuery) => {
    if (query.webview !== '1') return
    if (!query.platform || !query.orientation) {
      throw new Error('必須パラメータが不足しています')
    }
    platform.value = (query.platform as string) === 'android' ? 'android' : 'ios'
    orientation.value = (query.orientation as string) === 'landscape' ? 'landscape' : 'portrait'
    applyWebViewFontClass(isWebViewMode.value)
  }

  // iOS WebViewでFreeプラン上限に達している場合のダイアログ表示フラグ（persistしない）
  const __showFreePlanLimitDialog = ref(false)
  const openFreePlanLimitDialog = () => { __showFreePlanLimitDialog.value = true }
  const showFreePlanLimitDialog = computed(() => __showFreePlanLimitDialog.value)

  return { platform, orientation, isWebViewMode, isPortrait, isLandscape, initFromQuery, showFreePlanLimitDialog, __showFreePlanLimitDialog, openFreePlanLimitDialog }
},
{
  persist: {
    key: WEBVIEW_MODE_STORAGE_KEY,
    storage: sessionStorage,
    paths: [
      'platform',
      'orientation',
    ],
    // WebView 途中でのリロード（クエリなし）でも復元後に丸ゴを適用し直す
    afterRestore: (ctx) => {
      applyWebViewFontClass(ctx.store.isWebViewMode)
    },
  }
},
)
